/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 10 2024
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { PropsWithChildren, ForwardedRef, forwardRef, useRef, useEffect, useState, useCallback, useMemo } from "react";
import { timer, Subscription } from "rxjs";
import styled from "@emotion/styled";
import cloneDeep from "lodash-es/cloneDeep";

import { useStompClient } from "@/frameworks/libraries/network";
import { usePersistUser } from "@/frameworks/core/persistence";
import { useNetworkCommand } from "@/frameworks/core/network";
import { Configure } from "@/frameworks/libraries/utilities";
import { _P } from "@rfice/language";
import { TypographyCSS } from "@/frameworks/designStuff/components";

export type TypingRefType = {
	typing: () => void;
	done: () => void;
};

type Props = {
	roomID?: string;
	variant: ChatUI.Size;
};

type UserInfo = {
	id: string;
	name: string;
};

const LOGTAG = "#Typing#";

function TypingComponent({ roomID, ...props }: PropsWithChildren<Props>, ref: ForwardedRef<TypingRefType>) {
	const typingState = useRef<boolean>(false);
	const [typingUsers, setTypingUsers] = useState<UserInfo[]>([]);
	const remoteTyping = useRef<boolean>(false);
	const timeoutTimer = useRef<Subscription | null>(null);
	const typingTimer = useRef<Subscription | null>(null);
	const stompCommand = useNetworkCommand();
	const me = usePersistUser("me");

	const send_action = (type: "start" | "typing" | "end", callback?: () => void) => {
		if (roomID && me && Configure.userID) {
			timer(0).subscribe(() => {
				stompCommand.typing(roomID, {
					type,
					roomID,
					id: Configure.userID || "id",
					name: me.name || "name"
				});
				callback?.();
			});
		}
	};

	const typing = useCallback(() => {
		if (!typingState.current) {
			typingState.current = true;
			send_action("start");
		} else {
			send_action("typing");
		}

		checkTimeout();
	}, [roomID]);

	const done = useCallback(() => {
		send_action("end");
		typingState.current = false;
	}, [roomID, me]);

	const checkTimeout = () => {
		timeoutTimer.current?.unsubscribe();
		timeoutTimer.current = timer(3000).subscribe(() => {
			done();
		});
	};

	const start_typing_timer = (userID: string = "") => {
		typingTimer.current?.unsubscribe();
		typingTimer.current = timer(3000).subscribe(() => {
			typing_ended(userID);
		});
	};

	const initTypingState = () => {
		setTypingUsers([]);
		remoteTyping.current = false;
	};

	useEffect(() => {
		if (ref) {
			if (typeof ref === "function") {
				ref({ typing, done });
			} else {
				ref.current = { typing, done };
			}
		}
		initTypingState();

		return () => {
			if (ref) {
				if (typeof ref === "function") {
					ref(null);
				} else {
					ref.current = null;
				}
			}
		};
	}, [roomID]);

	const typing_ended = useCallback(
		(id: string) => {
			if (remoteTyping.current) {
				const newList = cloneDeep(typingUsers);
				newList.splice(
					newList.findIndex((item) => item.id === id),
					1
				);
				setTypingUsers(newList);
				if (newList.length <= 0) remoteTyping.current = false;
			}
		},
		[typingUsers, roomID]
	);

	useStompClient(
		{
			onMessageReceived(msg) {
				if (msg.type !== "typing") {
					return;
				}

				const { type, id, name } = msg.payload as ChatServices.Server.Typing.Data;
				// 내 입력 및 다른 방의 입력 중은 차단함
				if (id === Configure.userID || msg.payload.roomID !== roomID) {
					return;
				}

				if (["start", "typing"].includes(type)) {
					if (typingUsers.findIndex((item) => item.id === id) >= 0) {
						start_typing_timer(id);
						return;
					}
					const newList = cloneDeep(typingUsers);
					newList.unshift({ id, name });
					setTypingUsers(newList);
					remoteTyping.current = true;
					start_typing_timer(id);
				} else {
					typing_ended(id);
				}
			}
		},
		[me, typingUsers, roomID]
	);

	/**
	 * Update 05/02/2025:[FE][Design][Web Review] Space Chat: Displaying the "Writing…" message.
	 * @Author: DQTuan
	 */
	const TypingElementMemorized = useMemo(() => {
		return (
			<>
				<Indicator variant={props.variant}>
					<div className="dot"></div>
					<div className="dot"></div>
					<div className="dot"></div>
				</Indicator>
				{typingUsers.length === 1 && (
					<TypgingMessage variant={props.variant}>
						{_P("**{{name}}**", { useMD: true, name: typingUsers[0].name })}
						{_P("이 입력중...", { useMD: true })}
					</TypgingMessage>
				)}
				{typingUsers.length > 1 && (
					<TypgingMessage variant={props.variant}>
						{_P("**여러명**", { useMD: true })}
						{_P("이 입력중...", { useMD: true })}
					</TypgingMessage>
				)}
			</>
		);
	}, [typingUsers, props?.variant]);

	// Update 19/04/2025: fix error display typing message inside TypingContainer component.
	return <TypingContainer variant={props.variant}>{remoteTyping?.current && TypingElementMemorized}</TypingContainer>;
}

const Typing = forwardRef(TypingComponent);
Typing.displayName = "Typing";
export default Typing;

const TypingContainer = styled.div<{ variant: ChatUI.Size }>`
	display: flex;
	flex: 1 0 0;
	width: 100%;

	/**
	 * Update 24/12/2024: Update the style of typing container box based on the variant
	 * Variant:
	 * - fullSize: FullSize popup chat
	 * - miniPopup: MiniPopup chat
	 * - transparent: Workspace chat
	 */
	${({ variant }) => {
		switch (variant) {
			case "fullSize":
				return `
					position: absolute;
					bottom: 4px;
					height: 16px;
					padding: 0px 16px 4px 16px;
					gap: 6px;
					height: 16px;
				`;
			case "miniPopup":
				return `
					height: 16px;
					padding: 0px 16px 4px 16px;
					gap: 6px;
					position: absolute;
					bottom: 4px;
				`;
			case "transparent":
				return `
					padding: 0px 8px;
					height: 16px;
					gap: 6px;
					background-color: #FFFFFFCC;
				`;
		}
	}}
`;

const Indicator = styled.div<{ variant: ChatUI.Size }>`
	height: 100%;
	align-items: center;
	display: flex;
	justify-content: center;
	gap: 0.25rem;

	background: rgb(226 232 240);
	border-radius: 9999px;

	/**
	 * Update 24/12/2024: Update the style of indicator based on the variant
	 * Variant:
	 * - fullSize: FullSize popup chat
	 * - miniPopup: MiniPopup chat
	 * - transparent: Workspace chat
	 */
	${({ variant }) => {
		switch (variant) {
			case "fullSize":
				return `
					min-width: 28px;
					height: 12px;
				`;
			case "miniPopup":
				return `
					height: 12px;
					min-width: 28px;
				`;
			case "transparent":
				return `
                    height: 12px;
                    min-width: 28px;
                    margin-top: 2px;
                `;
		}
	}}

	.dot {
		border-radius: 9999px;
		height: 0.24rem;
		width: 0.24rem;

		background: rgba(148 163 184 / 1);
		opacity: 0;
		animation: blink 1s infinite;
	}
	.dot:nth-of-type(1) {
		animation-delay: 0.3333s;
	}
	.dot:nth-of-type(2) {
		animation-delay: 0.6666s;
	}
	.dot:nth-of-type(3) {
		animation-delay: 0.9999s;
	}

	@keyframes blink {
		50% {
			opacity: 1;
		}
	}

	@keyframes bulge {
		50% {
			transform: scale(1.05);
		}
	}
`;

const TypgingMessage = styled.div<{ variant: ChatUI.Size }>`
	display: flex;
	flex: 1;
	justify-content: flex-start;
	align-items: center;
	max-width: 100%;

	${TypographyCSS("Body6", "neutral_40_100")}

	p {
		font-weight: bold;
		max-width: calc(100% - 140px);
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
		font-style: normal;

		${TypographyCSS("Body6", "neutral_60_100")}
	}

	/**
	 * Update 24/12/2024: Style the typing message based on the variant
	 * Variant:
	 * - fullSize: FullSize popup chat
	 * - miniPopup: MiniPopup chat
	 * - transparent: Workspace chat
	 */
	${({ variant }) => {
		switch (variant) {
			case "fullSize":
				return `
					p:first-child {
						max-width: calc(100% - 80px) !important;
					}
					p:last-child {
						color: #AFB3BA;
						max-width: 100% !important;
					}
				`;
			case "miniPopup":
				return `
					p:first-child {
						max-width: calc(100% - 90px) !important;
					}
					p:last-child {
						color: #AFB3BA;
						max-width: 100% !important;
					}
				`;
			case "transparent":
				return `
                    p:first-child{
                        max-width: calc(100% - 90px) !important;
                    }
                    p:last-child {
                        color: #AFB3BA;
                        max-width: 100% !important;
                    }
                `;
		}
	}}
`;
